import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BRANCH_LABELS } from '@lorsamthai/ops-shared';
import {
  cancelRequest,
  commentOnRequest,
  getBranchRequest,
  reviewRequest,
  type BranchRequestDetail,
  type ReviewResult,
} from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtDateTime } from '../lib/format.js';
import { useAuth } from '../auth/context.js';
import {
  REQUEST_KIND_ICONS,
  REQUEST_KIND_LABELS,
  REQUEST_STATUS_LABELS,
  REQUEST_STATUS_TONES,
  URGENCY_LABELS,
  URGENCY_TONES,
} from '../lib/requestLabels.js';

export default function RequestDetail() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { me } = useAuth();
  const isSuper = me?.role === 'super_admin';

  const [req, setReq] = useState<BranchRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reviewNote, setReviewNote] = useState('');
  const [working, setWorking] = useState<'approve' | 'reject' | 'cancel' | 'comment' | null>(null);
  const [reviewResult, setReviewResult] = useState<ReviewResult | null>(null);

  const [commentBody, setCommentBody] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    getBranchRequest(id)
      .then(setReq)
      .catch((err) => setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (Number.isFinite(id)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!Number.isFinite(id)) return <div className="text-sm text-red-700">bad id</div>;
  if (loading) return <div className="text-sm text-slate-500">กำลังโหลด...</div>;
  if (error) return <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  if (!req) return <div className="text-sm text-slate-500">ไม่พบคำขอ</div>;

  const isOpen = req.status === 'submitted' || req.status === 'under_review';
  const isMine = me?.id === req.submitted_by;

  const review = async (action: 'approve' | 'reject') => {
    setWorking(action);
    setError(null);
    setReviewResult(null);
    try {
      const r = await reviewRequest(id, action, reviewNote.trim() || undefined);
      setReviewResult(r);
      setReviewNote('');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'review failed');
    } finally {
      setWorking(null);
    }
  };

  const cancel = async () => {
    if (!confirm('ยืนยันยกเลิกคำขอนี้?')) return;
    setWorking('cancel');
    setError(null);
    try {
      await cancelRequest(id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'cancel failed');
    } finally {
      setWorking(null);
    }
  };

  const submitComment = async (e: FormEvent) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setWorking('comment');
    setError(null);
    try {
      await commentOnRequest(id, commentBody.trim());
      setCommentBody('');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'comment failed');
    } finally {
      setWorking(null);
    }
  };

  const totalQty = req.branch_request_items.reduce((s, i) => s + i.qty, 0);

  return (
    <div className="space-y-5">
      <div>
        <Link to="/requests" className="text-xs text-slate-500 hover:underline">
          ← กลับรายการคำขอ
        </Link>
      </div>

      <header className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-2xl">{REQUEST_KIND_ICONS[req.kind]}</span>
              <h1 className="text-xl font-bold">{REQUEST_KIND_LABELS[req.kind]}</h1>
              <span className="font-mono text-sm text-slate-500">
                {req.req_ref ?? `#${req.id}`}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <Badge tone={REQUEST_STATUS_TONES[req.status]}>
                {REQUEST_STATUS_LABELS[req.status]}
              </Badge>
              {req.urgency !== 'normal' && (
                <Badge tone={URGENCY_TONES[req.urgency]}>{URGENCY_LABELS[req.urgency]}</Badge>
              )}
              <span>·</span>
              <span>
                จาก <strong>{BRANCH_LABELS[req.from_branch]}</strong>
              </span>
              {req.source_branch && (
                <>
                  <span>·</span>
                  <span>
                    ขอจาก <strong>{BRANCH_LABELS[req.source_branch]}</strong>
                  </span>
                </>
              )}
            </div>
          </div>

          {isOpen && (isMine || isSuper) && (
            <button
              onClick={cancel}
              disabled={working !== null}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-60"
            >
              ยกเลิกคำขอ
            </button>
          )}
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-4">
          <div>
            <dt className="text-slate-500">ส่งโดย</dt>
            <dd className="font-medium">{req.submitter?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">ส่งเมื่อ</dt>
            <dd className="font-medium">{fmtDateTime(req.submitted_at)}</dd>
          </div>
          {req.reviewer && (
            <>
              <div>
                <dt className="text-slate-500">ตรวจโดย</dt>
                <dd className="font-medium">{req.reviewer.name}</dd>
              </div>
              <div>
                <dt className="text-slate-500">ตรวจเมื่อ</dt>
                <dd className="font-medium">{fmtDateTime(req.reviewed_at)}</dd>
              </div>
            </>
          )}
        </dl>

        {req.reason && (
          <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
            <div className="text-xs font-medium text-slate-500">เหตุผล</div>
            {req.reason}
          </div>
        )}

        {req.review_note && (
          <div className="mt-2 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
            <div className="text-xs font-medium text-slate-500">โน้ตจากผู้ตรวจ</div>
            {req.review_note}
          </div>
        )}

        {(req.linked_tr_id || req.linked_po_id) && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {req.linked_tr_id && (
              <Link
                to="/transfers"
                className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-800 hover:bg-emerald-100"
              >
                → ใบโอนที่สร้างจากคำขอนี้ #{req.linked_tr_id}
              </Link>
            )}
            {req.linked_po_id && (
              <Link
                to={`/pos/${req.linked_po_id}`}
                className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-800 hover:bg-emerald-100"
              >
                → PO ที่สร้างจากคำขอนี้ #{req.linked_po_id}
              </Link>
            )}
          </div>
        )}
      </header>

      {/* Items */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm">
          <h2 className="font-semibold">รายการ ({req.branch_request_items.length} SKU)</h2>
          <span className="text-xs text-slate-500">รวม {totalQty} เส้น</span>
        </header>
        <ul className="divide-y divide-slate-100">
          {req.branch_request_items.map((it) => (
            <li key={it.sku} className="flex items-center justify-between px-4 py-3 text-sm">
              <div className="min-w-0">
                <Link
                  to={`/stock/${encodeURIComponent(it.sku)}`}
                  className="font-mono text-sm hover:underline"
                >
                  {it.sku}
                </Link>
                {it.note && <div className="mt-0.5 text-xs text-slate-500">{it.note}</div>}
              </div>
              <div className="ml-4 shrink-0 text-right">
                <span className="text-base font-semibold">{it.qty}</span>
                <span className="ml-1 text-xs text-slate-500">เส้น</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {reviewResult?.warnings && reviewResult.warnings.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-medium">มีเตือนตอนสร้าง PO:</div>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {reviewResult.warnings.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Review action — super only, only when active */}
      {isOpen && isSuper && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold">ตรวจคำขอ</h2>
          <textarea
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            rows={2}
            placeholder="โน้ต (ไม่บังคับ)"
            className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => review('approve')}
              disabled={working !== null}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {working === 'approve' ? 'กำลังอนุมัติ...' : '✓ อนุมัติ'}
            </button>
            <button
              onClick={() => review('reject')}
              disabled={working !== null}
              className="rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              {working === 'reject' ? 'กำลังปฏิเสธ...' : '✕ ปฏิเสธ'}
            </button>
          </div>
          {req.kind === 'transfer_request' && (
            <p className="mt-2 text-xs text-slate-500">
              อนุมัติแล้วระบบจะสร้างใบโอนจาก {req.source_branch ? BRANCH_LABELS[req.source_branch] : '—'}{' '}
              → {BRANCH_LABELS[req.from_branch]} อัตโนมัติ
            </p>
          )}
          {req.kind === 'purchase_request' && (
            <p className="mt-2 text-xs text-slate-500">
              ระบบจะรวม PO ตาม supplier หลักของ SKU แต่ละตัว — SKU ที่ยังไม่ตั้ง supplier จะถูกข้าม
            </p>
          )}
        </section>
      )}

      {/* Comments */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <header className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">
          ความเห็น ({req.comments.length})
        </header>
        {req.comments.length === 0 ? (
          <div className="px-4 py-3 text-xs text-slate-400">— ยังไม่มีความเห็น —</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {req.comments.map((c) => (
              <li key={c.id} className="px-4 py-3 text-sm">
                <div className="flex items-baseline gap-2 text-xs text-slate-500">
                  <span className="font-medium text-slate-700">
                    {c.author?.name ?? 'ไม่ทราบ'}
                  </span>
                  <span>·</span>
                  <span>{fmtDateTime(c.created_at)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{c.body}</p>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={submitComment} className="border-t border-slate-100 px-4 py-3">
          <textarea
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            rows={2}
            placeholder="พิมพ์ความเห็น..."
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              disabled={working !== null || !commentBody.trim()}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
            >
              {working === 'comment' ? 'กำลังส่ง...' : 'ส่งความเห็น'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
